import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import { pendingApprovalsSaga } from './pendingApprovalsSaga';
import type { RootState } from 'types';

/**
 * `GET /api/v2/approvals/pending-count` - the backend source of truth for
 * whether the persistent Home icon notification indicator (LeftSidebar.tsx)
 * should show. Lives here (app/store), not inside ReviewApproval's own
 * slice, because the Home icon renders on every route via LeftSidebar - its
 * badge needs to read this state on the very first app load, before the
 * user ever visits /review-approval. ReviewApproval's own slice only gets
 * its reducer/saga injected once `ReviewApprovalDashboard` itself mounts
 * (see useReviewApprovalSlice() in ReviewApproval/index.tsx), which is too
 * late for a badge shown on every page. Same "global concern, injected once
 * at the App root" precedent as globalCacheSlice.ts.
 */
export interface PendingApprovalsResponse {
  hasPendingActions: boolean;
  pendingCount: number;
}

export interface PendingApprovalsState {
  hasPendingActions: boolean;
  pendingCount: number;
  loading: boolean;
  error: any | null;
}

export const initialState: PendingApprovalsState = {
  hasPendingActions: false,
  pendingCount: 0,
  loading: false,
  error: null
};

const pendingApprovalsSlice = createSlice({
  name: 'pendingApprovals',
  initialState,
  reducers: {
    loadPendingApprovalsCount(state) {
      state.loading = true;
      state.error = null;
    },
    pendingApprovalsCountLoaded(
      state,
      action: PayloadAction<PendingApprovalsResponse>
    ) {
      // The backend response is the sole source of truth for the badge -
      // no local "viewed"/dismissed state is layered on top of this.
      state.hasPendingActions = action.payload.hasPendingActions;
      state.pendingCount = action.payload.pendingCount;
      state.loading = false;
    },
    loadPendingApprovalsCountError(state, action: PayloadAction<any>) {
      state.error = action.payload;
      state.loading = false;
    }
  }
});

export const {
  actions: pendingApprovalsActions,
  reducer: pendingApprovalsReducer
} = pendingApprovalsSlice;

export const selectHasPendingApprovals = (state: RootState) =>
  state.pendingApprovals?.hasPendingActions || false;
export const selectPendingApprovalsCount = (state: RootState) =>
  state.pendingApprovals?.pendingCount || 0;
export const selectPendingApprovalsLoading = (state: RootState) =>
  state.pendingApprovals?.loading || false;
export const selectPendingApprovalsError = (state: RootState) =>
  state.pendingApprovals?.error || null;

/**
 * Called once, at the App root (src/app/index.tsx), alongside
 * useGlobalCacheSlice()/useHomeSlice() - not from any one page - so the
 * reducer/saga exist from first paint, before /review-approval is ever
 * visited. Other call sites (e.g. ReviewApprovalDashboard's own mount
 * effect, or a future Approve/Reject/Delegate success handler) only need
 * `pendingApprovalsActions` to dispatch a refresh - they don't need to call
 * this hook again, same as MilestonesGrid's saga already dispatches
 * `homeActions.setSilentRefreshing(...)` without calling useHomeSlice().
 */
export const usePendingApprovalsSlice = () => {
  useInjectReducer({
    key: 'pendingApprovals',
    reducer: pendingApprovalsReducer
  });
  useInjectSaga({ key: 'pendingApprovals', saga: pendingApprovalsSaga });
  return { actions: pendingApprovalsActions };
};
